import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { api, on, type ManualCase, type ManualDoc } from "../api";
import i18n from "../i18n";
import { useTitle } from "../useTitle";
import { Board, Counts, Loading, Notice, PageHead, type Count } from "../components/Look";

/**
 * What a person still has to run, and the Jira housekeeping around it.
 *
 * COMPUTED ON THE BENCH, NOT HERE, and the page says so. It needs the plan,
 * scenarios.yaml and the XSNCF snapshot at once, and that snapshot is fetched
 * with Jira credentials that deliberately never leave the bench. So the rig
 * works it out at the start of a run - after probing itself, so the "skips
 * here" section is about the rig as it actually is - and publishes the answer.
 *
 * The consequence worth stating in the UI: this document is as old as the last
 * run, and its Jira half is as old as the last fetch. Both dates are shown,
 * because a coverage verdict from a three-week-old snapshot is a guess wearing
 * a verdict's clothes.
 */
const ORDER = ["design", "gap", "gated"] as const;
type Scope = (typeof ORDER)[number];

export default function Manual() {
  const { t } = useTranslation();
  useTitle(t("manual.title"));
  const [params, setParams] = useSearchParams();
  const [doc, setDoc] = useState<ManualDoc | null>(null);
  const [bench, setBench] = useState<{ id: number; name: string } | null>(null);
  const [benches, setBenches] = useState<{ id: number; name: string }[]>([]);
  const [at, setAt] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const benchId = params.get("bench");
  const scope = (params.get("scope") ?? "") as Scope | "";

  const load = useCallback(async () => {
    try {
      const d = await api.manual(benchId ? Number(benchId) : undefined);
      setDoc(d.manual ?? {});
      setBench(d.bench);
      setBenches(d.benches ?? []);
      setAt(d.at);
      setError("");
    } catch (e) {
      setError(String(e));
    } finally {
      setLoaded(true);
    }
  }, [benchId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!loaded) return <Loading label={t("common.loading")} />;
  if (error) return <p className="text-red-700 dark:text-red-400">{error}</p>;

  const cases = doc?.cases ?? [];
  if (cases.length === 0) {
    return (
      <section>
        <Head bench={bench} benches={benches} at={at} onPick={(id) => {
          const p = new URLSearchParams(params);
          if (id) p.set("bench", String(id)); else p.delete("bench");
          setParams(p);
        }} />
        <p className="mt-4 max-w-2xl text-sm text-slate-500">
          {/* Absence has two causes and they call for different reactions, so
              the page names both rather than showing a plausible zero. */}
          {t("manual.empty")}
        </p>
      </section>
    );
  }

  const counts = doc?.counts ?? {};
  const minutes = doc?.minutes ?? {};
  const shown = scope ? cases.filter((c) => c.why === scope) : cases;

  return (
    <section>
      <Head bench={bench} benches={benches} at={at} cases={cases.length} onPick={(id) => {
        const p = new URLSearchParams(params);
        if (id) p.set("bench", String(id)); else p.delete("bench");
        setParams(p);
      }} />

      <Freshness doc={doc} />

      <Counts
        items={ORDER.map((k): Count => {
          const gatedUnknown = k === "gated" && doc?.caps_known === false;
          const n = gatedUnknown ? 0 : (counts[k] ?? 0);
          const name = doc?.scopes?.[k]?.short ?? k;
          // A tile reading 0 for a number that was never computed answers
          // "which cases skip here" with "none", inches above a sentence
          // saying the answer is unknown.
          const label = gatedUnknown
            ? `${name}: ${t("common.dash")}`
            : `${n} ${name} (${t("manual.minutes", { count: minutes[k] ?? 0 })})`;
          return {
            n,
            label,
            tone: k === "gated" ? "off" : k === "design" ? "plain" : "warn",
            active: scope === k,
            onClick: gatedUnknown
              ? undefined
              : () => {
                  const p = new URLSearchParams(params);
                  if (scope === k) p.delete("scope"); else p.set("scope", k);
                  setParams(p);
                },
          };
        })}
      />

      {/* Said as a sentence, not as a dash on a tile: the count is not zero,
          it is unknown, and those call for different reactions. */}
      {doc?.caps_known === false && (
        <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">{t("manual.notKnown")}</p>
      )}

      {scope && doc?.scopes?.[scope] && (
        <p className="mt-3 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          {doc.scopes[scope].why}
        </p>
      )}

      {doc?.shed ? (
        <p className="mt-3 text-xs text-amber-900 dark:text-amber-200">
          {t("manual.shed", { count: doc.shed })}
        </p>
      ) : null}

      <Board as="ul" className="mt-4">
        {shown.map((c) => <Case key={c.id} c={c} />)}
      </Board>

      {(doc?.findings?.length ?? 0) > 0 && <Findings doc={doc} />}
    </section>
  );
}

function Head({ bench, benches, at, onPick, cases }: {
  bench: { id: number; name: string } | null;
  benches: { id: number; name: string }[];
  at: number | null;
  onPick: (id: number | null) => void;
  /** How much work the document holds, which is what the page is asked. */
  cases?: number;
}) {
  const { t } = useTranslation();
  return (
    <PageHead
      title={
        <span className="flex flex-wrap items-baseline gap-3">
          <span className="font-semibold text-slate-900 dark:text-slate-100">{t("manual.heading")}</span>
          {benches.length > 1 && (
            <select
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
              value={bench?.id ?? ""}
              onChange={(e) => onPick(e.target.value ? Number(e.target.value) : null)}
            >
              {benches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          {bench && benches.length <= 1 && (
            <span className="text-slate-500">{t("manual.benchOn", { bench: bench.name })}</span>
          )}
        </span>
      }
      answer={cases ? t("manual.answerCases", { count: cases }) : undefined}
      after={at ? <p className="text-sm text-slate-500">{t("manual.published", { when: on(at) })}</p> : undefined}
    />
  );
}

/** Both ages, because either one being stale changes what the page is worth. */
function Freshness({ doc }: { doc: ManualDoc | null }) {
  const j = doc?.jira;
  const notes: string[] = [];
  if (j && !j.present) {
    notes.push(i18n.t("manual.jiraAbsent"));
  } else if (j && j.age_days != null && j.age_days > 14) {
    notes.push(
      i18n.t("manual.jiraStale", {
        days: Math.round(j.age_days),
        when: j.fetched,
      }),
    );
  }
  if (doc?.caps_known === false) {
    notes.push(i18n.t("manual.capsUnknown"));
  }
  if (notes.length === 0) return null;
  return (
    <Notice tone="warn">
      {notes.map((n) => <p key={n}>{n}</p>)}
    </Notice>
  );
}

function Case({ c }: { c: ManualCase }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // The VALUES here - a priority, an access level, a capability name, the spec
  // reference - are what the bench published. They are data, shown as stored;
  // only the words around them belong to this page.
  const meta = [
    c.priority && t("manual.metaPriority", { priority: c.priority }),
    c.level && t("manual.metaLevel", { level: c.level }),
    c.spec,
    t("manual.metaMinutes", { count: c.minutes }),
    c.missing_caps?.length &&
      t("manual.metaNeeds", { caps: c.missing_caps.join(", ") }),
  ].filter(Boolean) as string[];

  return (
    <li>
      <button
        className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="whitespace-nowrap font-mono text-sm font-semibold">{c.id}</span>
        <span className="text-sm">{c.name}</span>
        {c.sync && (
          <span className={`ml-auto rounded px-1.5 py-0.5 text-xs ${
            c.sync.verdict === "synced"
              ? "bg-success-100 text-success-800 dark:bg-success-950 dark:text-success-300"
              : c.sync.verdict === "drifted"
                ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}>
            {i18n.t(`manual.verdict.${c.sync.verdict}` as "manual.verdict.synced", {
              defaultValue: c.sync.verdict,
            })}
          </span>
        )}
      </button>
      <p className="px-3 pb-2 text-xs text-slate-500">{meta.join(" · ")}</p>

      {open && (
        <div className="border-t border-slate-200 px-3 py-2 text-sm dark:border-slate-800">
          {c.preconditions && (
            <p className="mb-2 text-slate-600 dark:text-slate-400">
              <b>{t("manual.before")}</b> {c.preconditions}
            </p>
          )}
          <p className="mb-1 text-xs font-medium text-slate-500">
            {t("manual.procedure", {
              total: c.steps_total,
              manual: c.steps_manual,
            })}
          </p>
          <ol className="space-y-1">
            {c.steps.map((s) => (
              <li key={s.n} className="flex gap-2">
                <span className="w-6 shrink-0 text-right text-xs text-slate-500">
                  {s.n}
                </span>
                {/* The automatable ones are shown but recessed: they are
                    context for the steps around them, not work. */}
                <span className={s.manual ? "" : "text-slate-500"}>{s.text}</span>
              </li>
            ))}
          </ol>
          {c.steps_total > c.steps.length && (
            <p className="mt-1 text-xs text-slate-500">
              {t("manual.furtherSteps", { count: c.steps_total - c.steps.length })}
            </p>
          )}
          {c.expected && (
            <p className="mt-2"><b>{t("manual.passWhen")}</b> {c.expected}</p>
          )}
          {c.sync && <Sync sync={c.sync} />}
        </div>
      )}
    </li>
  );
}

function Sync({ sync }: { sync: NonNullable<ManualCase["sync"]> }) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 border-t border-slate-200 pt-2 dark:border-slate-800">
      <p className="text-xs font-medium text-slate-500">{t("manual.jira")}</p>
      {sync.issues.length > 0 ? (
        <p className="mt-1 flex flex-wrap gap-2">
          {sync.issues.map((i) => (
            <a key={i.key} href={i.url} target="_blank" rel="noopener"
               className="text-brand-700 underline dark:text-sky-300">
              {i.key}{i.done ? t("manual.closed") : ""}
            </a>
          ))}
        </p>
      ) : (
        <p className="mt-1 text-slate-600 dark:text-slate-400">{sync.action}</p>
      )}
      {sync.diffs.length > 0 && (
        <table className="mt-2 w-full border-collapse text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="border-b border-slate-200 py-1 dark:border-slate-800">
                {t("manual.colField")}
              </th>
              <th className="border-b border-slate-200 py-1 dark:border-slate-800">
                {t("manual.colJira")}
              </th>
              <th className="border-b border-slate-200 py-1 dark:border-slate-800">
                {t("manual.colPlan")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sync.diffs.map((d) => (
              <tr key={d.field}>
                <td className="py-1 pr-2 align-top font-medium">{d.field}</td>
                <td className="py-1 pr-2 align-top">{d.jira}</td>
                <td className="py-1 align-top">{d.plan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {sync.review.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-slate-500">
            {t("manual.wordingDiffers", { count: sync.review.length })}
          </summary>
          <ul className="mt-1 space-y-1 text-xs">
            {sync.review.map((d) => (
              <li key={d.field}>
                <b>{d.field}</b> — Jira: {d.jira || "-"} · plan: {d.plan || "-"}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Findings({ doc }: { doc: ManualDoc | null }) {
  const { t } = useTranslation();
  return (
    <section className="mt-8">
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("manual.issueProblems")}
      </h2>
      <ul className="mt-2 space-y-2">
        {(doc?.findings ?? []).map((f) => (
          <li key={f.key + f.kind}
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
            <p>
              <a href={f.url} target="_blank" rel="noopener"
                 className="text-brand-700 underline dark:text-sky-300">{f.key}</a>{" "}
              {f.title}{" "}
              <span className="rounded bg-amber-200 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                {f.kind}
              </span>
            </p>
            <p className="mt-1 text-slate-600 dark:text-slate-400">{f.detail}</p>
            <p className="mt-1"><b>{t("manual.action")}</b> {f.action}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
